import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { NoAccess } from "@/components/patterns";

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: string;
  anyOf?: string[];
  allOf?: string[];
  /** Only these role codes may open the page (e.g. ["SUPER_ADMIN"]) */
  roles?: string[];
}

export function ProtectedRoute({
  children,
  permission,
  anyOf,
  allOf,
  roles,
}: ProtectedRouteProps) {
  const { user, isAuthenticated, loading, hasPermission, hasAnyPermission, hasAllPermissions } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background text-foreground gap-3">
        <Loader2 className="size-8 text-primary animate-spin" />
        <span className="text-sm text-muted-foreground font-medium">
          Verifying session...
        </span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // A password chosen by someone else must be replaced before anything else
  if (user?.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  // The company requires two-step verification for this role: it must be set up before anything else opens
  if (user?.two_factor_required && !user?.two_factor_enabled && location.pathname !== "/setup-two-step" && location.pathname !== "/change-password") {
    return <Navigate to="/setup-two-step" replace />;
  }

  // Not allowed here: say so, with a way back, rather than silently sending the person elsewhere
  if (roles && roles.length > 0 && !roles.includes(String(user?.UserRole?.RoleCode || "").toUpperCase())) {
    return <NoAccess />;
  }

  if (permission && !hasPermission(permission)) {
    return <NoAccess />;
  }

  if (anyOf && anyOf.length > 0 && !hasAnyPermission(anyOf)) {
    return <NoAccess />;
  }

  if (allOf && allOf.length > 0 && !hasAllPermissions(allOf)) {
    return <NoAccess />;
  }

  return <>{children}</>;
}
